import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ExternalLink, Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { CLINIC_INFO } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[var(--bg-dark)] text-white">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div className="lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-5">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[var(--color-primary)] font-bold text-lg">Á</span>
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold leading-tight">Áurea Dental</span>
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Integral</span>
              </div>
            </Link>

            <p className="text-gray-300/90 text-sm leading-relaxed mb-6">
              Odontología estética y clínica de alto nivel. {CLINIC_INFO.yearsExperience}+ años transformando
              sonrisas en {CLINIC_INFO.city}.
            </p>

            <div className="flex gap-3">
              <a
                href={CLINIC_INFO.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-dark)]"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href={CLINIC_INFO.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-dark)]"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Navegación
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Inicio
                </Link>
              </li>
              <li>
                <Link to="/servicios" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Servicios
                </Link>
              </li>
              <li>
                <Link to="/equipo" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Equipo
                </Link>
              </li>
              <li>
                <Link to="/testimonios" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Testimonios
                </Link>
              </li>
              <li>
                <Link to="/#agendar" className="text-gray-300 hover:text-white transition-colors text-sm">
                  Agendar cita
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Contacto
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${CLINIC_INFO.phone}`}
                  className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <Phone size={18} className="mt-0.5 flex-shrink-0 text-[var(--color-accent)]" />
                  <span className="text-sm">{CLINIC_INFO.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CLINIC_INFO.email}`}
                  className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <Mail size={18} className="mt-0.5 flex-shrink-0 text-[var(--color-accent)]" />
                  <span className="text-sm">{CLINIC_INFO.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={CLINIC_INFO.mapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-300 hover:text-white transition-colors group"
                >
                  <MapPin size={18} className="mt-0.5 flex-shrink-0 text-[var(--color-accent)]" />
                  <span className="text-sm">
                    {CLINIC_INFO.address}
                    <span className="inline-flex items-center gap-1 ml-2 text-[var(--color-accent)] text-xs">
                      Abrir en Maps <ExternalLink size={12} />
                    </span>
                  </span>
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Horarios
            </h4>
            <div className="flex items-start gap-3">
              <Clock size={18} className="mt-0.5 flex-shrink-0 text-[var(--color-accent)]" />
              <div className="text-sm">
                <p className="text-gray-300">{CLINIC_INFO.hours.weekdays}</p>
                <p className="text-gray-300">{CLINIC_INFO.hours.saturday}</p>
                <p className="text-gray-400">{CLINIC_INFO.hours.sunday}</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <p className="text-xs text-gray-300">
                <strong className="text-white">Estacionamiento disponible</strong>
              </p>
              <p className="text-xs text-gray-400">Acceso para personas con discapacidad</p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {currentYear} Áurea Dental Integral. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Aviso de Privacidad
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">
                Términos
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

